import { useContext, useState } from "react";
import { ButtonDS, GlobalIcon } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../context/i18n.context";
import numberFormat from "../../../../../utils/Utils";

interface UpgradeFunnelProps {
  currentCapital: number;
  /** null = sin prima (cohorte SCOR FR): no se calcula la diferencia con 0. */
  currentPremium: number | null;
  onBack: () => void;
}

const RATE_PER_EUR_MONTH = 0.000065;
const MAX_CAPITAL = 1000000;
const STEP = 10000;

const UpgradeFunnel = ({
  currentCapital,
  currentPremium,
  onBack,
}: UpgradeFunnelProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);

  const [selectedCapital, setSelectedCapital] = useState(
    Math.min(currentCapital + 100000, MAX_CAPITAL)
  );
  const [healthDeclared, setHealthDeclared] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const newPremium = +(selectedCapital * RATE_PER_EUR_MONTH).toFixed(2);
  const premiumDiff =
    null === currentPremium ? null : +(newPremium - currentPremium).toFixed(2);
  const sliderPercent =
    ((selectedCapital - currentCapital) / (MAX_CAPITAL - currentCapital)) * 100;

  const cardStyle: React.CSSProperties = {
    backgroundColor: "#fff",
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    padding: 24,
  };

  // ── SUCCESS STATE ─────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 560 }}>
        <div style={{ ...cardStyle, textAlign: "center", padding: "48px 32px" }}>
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              backgroundColor: "#ecfdf5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <GlobalIcon iconName="CircleCheckFilledIcon" size="S" color="#059669" />
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: "#065f46", margin: "0 0 12px" }}>
            {translate("newDashboard.upgrade.successTitle")}
          </h1>
          <p style={{ fontSize: 15, color: "#6b7280", lineHeight: 1.6, margin: "0 0 32px" }}>
            {translate("newDashboard.upgrade.successDesc")}
          </p>

          {/* Summary */}
          <div
            style={{
              backgroundColor: "#f9fafb",
              borderRadius: 10,
              padding: 16,
              marginBottom: 28,
              textAlign: "left",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #e5e7eb" }}>
              <span style={{ fontSize: 14, color: "#6b7280" }}>{translate("newDashboard.upgrade.currentCapital")}</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: "#9ca3af" }}>{numberFormat(currentCapital, "capital")}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{translate("newDashboard.upgrade.newCapital")}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#059669" }}>{numberFormat(selectedCapital, "capital")}</span>
            </div>
          </div>

          <ButtonDS
            label={translate("newDashboard.upgrade.backButton")}
            buttonType="primary"
            size="40"
            onClick={onBack}
          />
        </div>
      </div>
    );
  }

  // ── MAIN FUNNEL ───────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 640 }}>
      {/* Back + Header */}
      <div>
        <button
          onClick={onBack}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            border: "none",
            background: "none",
            cursor: "pointer",
            color: "#6b7280",
            fontSize: 14,
            fontWeight: 500,
            padding: 0,
            marginBottom: 12,
          }}
        >
          <GlobalIcon iconName="ChevronDown" size="XS" color="#6b7280" />
          <span>{translate("newDashboard.upgrade.backButton")}</span>
        </button>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: "#111827", margin: 0 }}>
          {translate("newDashboard.upgrade.title")}
        </h1>
        <p style={{ fontSize: 15, color: "#6b7280", marginTop: 4 }}>
          {translate("newDashboard.upgrade.subtitle")}
        </p>
      </div>

      {/* Current capital */}
      <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              backgroundColor: "#f3f4f6",
              borderRadius: 10,
              flexShrink: 0,
            }}
          >
            <GlobalIcon iconName="ShieldLockIcon" size="XS" color="#9ca3af" />
          </div>
          <div>
            <p style={{ fontSize: 13, fontWeight: 500, color: "#9ca3af", margin: 0 }}>
              {translate("newDashboard.upgrade.currentCapital")}
            </p>
            <p style={{ fontSize: 20, fontWeight: 700, color: "#6b7280", margin: 0 }}>
              {numberFormat(currentCapital, "capital")}
            </p>
          </div>
        </div>
      </div>

      {/* Slider card */}
      <div style={{ ...cardStyle, border: "2px solid #e0e7ff" }}>
        <p style={{ fontSize: 13, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, margin: "0 0 16px" }}>
          {translate("newDashboard.upgrade.newCapital")}
        </p>

        {/* Big number */}
        <div style={{ textAlign: "center", margin: "0 0 24px" }}>
          <p style={{ fontSize: 40, fontWeight: 800, color: "#312e81", margin: 0 }}>
            {numberFormat(selectedCapital, "capital")}
          </p>
        </div>

        {/* Slider */}
        <div style={{ padding: "0 8px" }}>
          <input
            type="range"
            min={currentCapital}
            max={MAX_CAPITAL}
            step={STEP}
            value={selectedCapital}
            onChange={(e) => setSelectedCapital(Number(e.target.value))}
            style={{
              width: "100%",
              height: 8,
              appearance: "none",
              WebkitAppearance: "none",
              background: `linear-gradient(to right, #4f46e5 0%, #4f46e5 ${sliderPercent}%, #e5e7eb ${sliderPercent}%, #e5e7eb 100%)`,
              borderRadius: 4,
              outline: "none",
              cursor: "pointer",
            } as any}
          />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
            <span style={{ fontSize: 12, color: "#9ca3af" }}>
              {numberFormat(currentCapital, "capital")}
            </span>
            <span style={{ fontSize: 12, color: "#9ca3af" }}>
              {numberFormat(MAX_CAPITAL, "capital")}
            </span>
          </div>
        </div>
      </div>

      {/* Price estimation */}
      <div style={{ ...cardStyle, backgroundColor: "#eef2ff", border: "1px solid #c7d2fe" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: "#6366f1", margin: 0 }}>
            {translate("newDashboard.upgrade.estimatedPremium")}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 32, fontWeight: 800, color: "#312e81" }}>
            {numberFormat(newPremium, "price")}
          </span>
          <span style={{ fontSize: 15, fontWeight: 500, color: "#6366f1" }}>
            {translate("newDashboard.upgrade.perMonth")}
          </span>
        </div>
        {null !== premiumDiff && premiumDiff > 0 && (
          <p style={{ fontSize: 14, fontWeight: 600, color: "#4338ca", margin: "0 0 12px" }}>
            {translate("newDashboard.upgrade.additionalCost").replace(
              "{amount}",
              numberFormat(premiumDiff, "price")
            )}
          </p>
        )}
        <p style={{ fontSize: 12, color: "#6366f1", fontStyle: "italic", margin: 0, lineHeight: 1.5 }}>
          {translate("newDashboard.upgrade.disclaimer")}
        </p>
      </div>

      {/* Health declaration */}
      <div style={cardStyle}>
        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={healthDeclared}
            onChange={(e) => setHealthDeclared(e.target.checked)}
            style={{
              width: 20,
              height: 20,
              marginTop: 2,
              accentColor: "#4f46e5",
              cursor: "pointer",
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
            {translate("newDashboard.upgrade.healthDeclaration")}
          </span>
        </label>
      </div>

      {/* Submit */}
      <ButtonDS
        label={translate("newDashboard.upgrade.submitButton")}
        buttonType="primary"
        size="40"
        disabled={!healthDeclared || selectedCapital <= currentCapital}
        onClick={() => setSubmitted(true)}
      />

      {/* Slider thumb CSS */}
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #4f46e5;
          border: 4px solid #fff;
          box-shadow: 0 2px 8px rgba(79,70,229,0.3);
          cursor: pointer;
          margin-top: -10px;
        }
        input[type="range"]::-moz-range-thumb {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #4f46e5;
          border: 4px solid #fff;
          box-shadow: 0 2px 8px rgba(79,70,229,0.3);
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default UpgradeFunnel;
